import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Step, StepButton, StepContent, Stepper, useMediaQuery, useTheme } from "@mui/material";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { OrderDetailsText } from "../../../Account/Orders/OrderDetailsAccordion/utils/OrderDetailsAccordion-types";
import { getCurrentBreakpoint, getDateFormat, } from "../../../Account/Subscriptions/ToggleableOrdersList/ToggleableOrdersList-utils";
import { useCompany } from "../../../Shared/components/CompanyProvider/CompanyProvider";
import { PaymentForm } from "../../../Shared/components/PaymentForm/PaymentForm";
import { usePaymentForm } from "../../../Shared/components/PaymentForm/usePaymentForm";
import Loading from "../../../Shared/components/loading";
import { BillingInformationContext } from "../../../Account/BillingInformation/BillingInformationContext";
import { Tokens } from "../../../Shared/sharedTypes/Tokens";
var PaymentStepperText;
(function (PaymentStepperText) {
    PaymentStepperText["SUCCESS"] = "All payments and updates have been successfully processed.";
    PaymentStepperText["ORDER_NUMBER"] = "Order #{orderNumber}";
})(PaymentStepperText || (PaymentStepperText = {}));
export const PaymentStepper = ({ steps, loading, nextOrderStep }) => {
    const [activeStep, setActiveStep] = useState(nextOrderStep);
    const { setBalance, setIsFormSubmitted } = usePaymentForm();
    const { setDoesPaymentIntentExist, setPaymentIntentId } = useContext(BillingInformationContext);
    const { culture } = useCompany();
    const theme = useTheme();
    const isXS = useMediaQuery(theme.breakpoints.up("xs"), { noSsr: true }); // 320px - 600px
    const isSmall = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true }); // 600px - 960px
    const isMedium = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true }); // 960px - 1280px
    const isLarge = useMediaQuery(theme.breakpoints.up("lg"), { noSsr: true }); // 1280px and up
    const currentBreakpoint = getCurrentBreakpoint(isXS, isSmall, isMedium, isLarge);
    const dateFormat = getDateFormat(currentBreakpoint);
    const orientation = isMedium ? "horizontal" : "vertical";
    const weekStartDateTitle = OrderDetailsText.THE_WEEK_OF;
    const allStepsCompleted = () => {
        return steps.every((s) => s.completed);
    };
    // Ensures a new payment intent is created for each order
    const clearPaymentIntentState = () => {
        setIsFormSubmitted(false);
        setPaymentIntentId(undefined);
        setDoesPaymentIntentExist(false);
    };
    const handleStep = (step) => () => {
        setActiveStep(step);
        const activeStep = steps[step];
        if (!activeStep.completed) {
            clearPaymentIntentState();
        }
        /**
         * When a step is clicked, set the balance for that step.
         * This will ensure the payment form has the correct balance.
         */
        setBalance(steps[step].balance);
    };
    const isMultiStep = steps.length > 1;
    // Show the PaymentForm in the StepContent if displaying vertically (on mobile) and all payments are not complete
    const renderInlinePaymentForm = orientation === "vertical" && !allStepsCompleted();
    // Show the PaymentForm outside of the StepContent if displaying horizontally OR there is only one order to pay for
    const renderOutsidePaymentForm = (orientation === "horizontal" || steps.length === 1) && !allStepsCompleted();
    // Ensure activeStep gets updated when nextOrderStep changes.
    useEffect(() => {
        if (activeStep !== nextOrderStep) {
            setActiveStep(nextOrderStep);
            const activeStep = steps[nextOrderStep];
            setBalance(activeStep.balance);
            if (!activeStep.completed) {
                clearPaymentIntentState();
            }
        }
    }, [nextOrderStep]);
    return (_jsxs(Box, Object.assign({ sx: { p: 2 } }, { children: [_jsx(Stepper, Object.assign({ alternativeLabel: isMedium, nonLinear: true, activeStep: activeStep, sx: { mb: 3 }, orientation: orientation }, { children: isMultiStep &&
                    steps.map((step, index) => {
                        const weekStartDateDetail = moment(step.weekOf).locale(culture.cultureInfo).format(dateFormat);
                        return (_jsxs(Step, Object.assign({ completed: step.completed, disabled: loading }, { children: [_jsx(StepButton, Object.assign({ onClick: handleStep(index), optional: `${weekStartDateTitle}: ${weekStartDateDetail}` }, { children: PaymentStepperText.ORDER_NUMBER.replace(Tokens.OrderNumber, step.orderNumber) })), renderInlinePaymentForm && (_jsx(StepContent, { children: _jsx(PaymentForm, { disabled: step.completed }) }))] }), step.orderNumber));
                    }) })), renderOutsidePaymentForm && _jsx(PaymentForm, { disabled: steps[activeStep].completed }), allStepsCompleted() && _jsx(Loading, { success: true, text: PaymentStepperText.SUCCESS })] })));
};
